import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Paper,
  IconButton,
  TableHead,
  CircularProgress,
  Alert,
  Modal,
  Typography,
  useMediaQuery,
  GlobalStyles, 
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";

// Import all previews
import ARMHSProgressNotePreview from "./Preview/ARMHSProgressNotePreview";
import ARMHSCrisisPlanPreview from "./Preview/ARMHSCrisisPlanPreview";
import ARMHSDischargeSummaryPreview from "./Preview/ARMHSDischargeSummaryPreview";
import ARMHSITPPreview from "./Preview/ARMHSITPPreview";
import ARMHSFunctionalAssessmentPreview from "./Preview/ARMHSFunctionalAssessmentPreview";
import ARMHSFunctionalAssessmentNewPreview from "./Preview/ARMHSFunctionalAssessmentNewPreview";
import DiagnosticAssessmentPreview from "./Preview/DiagnosticAssessmentPreview";
import AdultDiagnosticAssessmentPreview from "./Preview/AdultDiagnosticAssessmentPreview";
import AODASupervisionNotePreview from "./Preview/AODASupervisionNotePreview";
import GAD7Preview from "./Preview/GAD7Preview";
import CollateralNotePreview from "./Preview/CollateralNotePreview";
import NurseVisitPreview from "./Preview/NurseVisitPreview";
import PostpartumVisitPreview from "./Preview/PostpartumVisitPreview";
import PrenatalVisitPreview from "./Preview/PrenatalVisitPreview";
import IntakeFormPreview from "./Preview/IntakeFormPreview";
import BirthOverviewPreview from "./Preview/BirthOverviewPreview";
import PHQ9Preview from "./Preview/PHQ9Preview";
import BaselineQuestionairePreview from "./Preview/BaselinePreview";
import ClientSessionVerificationPreview from "./Preview/ClientSessionpreview";
import MHISClientFormPreview from "./Preview/MHISClientpreview";
import OBGYNPreview from "./Preview/OBGYNPreview";

// Pagination Controls
function TablePaginationActions({ count, page, rowsPerPage, onPageChange }) {
  const theme = useTheme();
  const totalPages = Math.ceil(count / rowsPerPage);
  return (
    <Box sx={{ display: "flex", gap: 1, p: "0 16px" }}>
      <IconButton
        onClick={(e) => onPageChange(e, page - 1)}
        disabled={page === 0}
      >
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={(e) => onPageChange(e, page + 1)}
        disabled={page + 1 >= totalPages}
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
    </Box>
  );
}

const FormsList = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewType, setPreviewType] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleClosePreview = useCallback(() => {
    setOpenPreview(false);
    setPreviewData(null);
    setPreviewType(null);
  }, []);

  // Helper function to check if signature exists
  const hasSignature = (value) => {
    if (!value) return false;
    if (value === null || value === undefined) return false;
    if (value === "null" || value === "undefined") return false;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed === "" || trimmed === "EMPTY") return false;
      return true;
    }
    return false;
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Fetch paginated forms
  const fetchForms = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("neuro_patientportal_auth_token");
      if (!token) throw new Error("Authentication token not found.");

      const res = await fetch(
        `${API_BASE_URL}/PatientPortal/GetPatientForms?PageNumber=${
          page + 1
        }&pageSize=${rowsPerPage}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.status === 404) {
        setForms([]);
        setTotalCount(0);
        setError("No forms found for the patient.");
        return;
      }

      if (!res.ok) throw new Error("Failed to fetch forms");

      const data = await res.json();

      if (!Array.isArray(data) || data.length === 0) {
        setForms([]);
        setTotalCount(0);
        setError("No forms found for the patient.");
        return;
      }

      setForms(data);
      setTotalCount(data[0]?.TotalRows || data.length);
    } catch (err) {
      setError(err.message);
      setForms([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, API_BASE_URL]);

  // Handle view click
  const handleView = useCallback((id, title) => {
    const normalized = (title || "").toLowerCase().trim();
    const formMap = {
      "armhs progress note": "ARMHSProgressNote",
      "armhs crisis plan": "ARMHSCrisisPlan",
      "armhs discharge summary": "ARMHSDischargeSummary",
      "armhs itp": "ARMHSITP",
      "armhs functional assessment new": "ARMHSFunctionalAssessmentNew",
      "armhs functional assessment": "ARMHSFunctionalAssessment",
      "adult diagnostic assessment": "AdultDiagnosticAssessment",
      "diagnostic assessment": "DiagnosticAssessment",
      "collateral note": "CollateralNote",
      "aoda supervision": "AODASupervisionNote",
      "aoda collaboration": "AODASupervisionNote",
      "gad7": "GAD7",
      "nurse visit": "NurseVisit",
      "postpartum visit": "PostpartumVisit",
      "prenatal visit": "PrenatalVisit",
      "intake form": "IntakeForm",
      "birth overview": "BirthOverview",
      "phq9": "PHQ9",
      "baseline questionnaire": "BaselineQuestionaire",
      "baseline questionaire": "BaselineQuestionaire",
      "client session verification": "ClientSessionVerification",
      "mhis client form": "MHISClientForm",
      "mhis client level data": "MHISClientForm",
      "obgyn": "OBGYN",
    };

    const formType =
      Object.entries(formMap).find(([k]) => normalized.includes(k))?.[1] ||
      "Generic";

    setPreviewData({ patientFormId: id, formTitle: title });
    setPreviewType(formType);
    setOpenPreview(true);
  }, []);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  // --- STYLES ---

  const stickyHeaderSx = {
    fontWeight: "bold",
    fontSize: "1rem",
    zIndex: 10,
    backgroundColor: "#fff",
    color: "#000",
    ".dark-theme &": {
      backgroundColor: "#0e1726", 
      color: "#fff !important", 
      borderBottom: "1px solid #1f2937",
    },
  };

  const bodyCellStyle = {
    ".dark-theme &": {
      color: "#fff !important",
      borderBottom: "1px solid #1f2937",
    },
  };

  const breadcrumbSx = {
    ".dark-theme &": {
      backgroundColor: "#0e1726 !important", 
      borderRadius: "8px",
      padding: "20px",
      marginBottom: "24px",
      "& h5": { 
        color: "#fff !important",
        margin: 0,
        marginBottom: "8px",
        fontWeight: "bold",
        fontSize: "1.25rem"
      },
      "& .breadcrumb": {
        marginBottom: 0,
        "& li": { color: "#fff !important" },
        "& li a": { color: "#fff !important" },
        "& i": { color: "#fff !important", verticalAlign: "middle" }
      }
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", md: "80%", lg: "70%" },
    borderRadius: 2,
    boxShadow: 24,
    overflow: "hidden",
    
    // Default Light Mode
    bgcolor: "#fff",
    color: "#000",

    // Dark Mode Override
    ".dark-theme &": {
      bgcolor: "#0e1726", 
      color: "#fff",
      border: "1px solid #3b3f5c"
    },
  };

  // --- PREVIEW WRAPPER (DARK MODE FIXES) ---
  const previewWrapperSx = {
    maxHeight: "90vh",
    overflow: "auto",
    p: 2,
    
    // DEEP DARK MODE OVERRIDES
    ".dark-theme &": {
      // 1. GLOBAL TEXT & CONTAINERS
      color: "#fff !important",
      "& .MuiPaper-root, & .MuiCard-root, & .MuiBox-root, & .MuiAccordion-root": {
        backgroundColor: "#0e1726 !important",
        color: "#fff !important",
        backgroundImage: "none !important",
        boxShadow: "none !important",
        border: "none",
      },

      // 2. TEXT VISIBILITY
      "& h1, & h2, & h3, & h4, & h5, & h6, & p, & span, & div, & label": {
        color: "#fff !important",
      },
      "& [style*='background-color: white'], & [style*='background-color: #fff']": {
        backgroundColor: "#0e1726 !important",
      },

      // 3. INPUTS & TEXTAREAS
      "& input, & textarea, & select": {
        backgroundColor: "#1b2e4b !important", 
        border: "1px solid #3b3f5c !important", 
        color: "#fff !important",
        borderRadius: "4px",
        padding: "8px 10px", 
      },
      "& .MuiOutlinedInput-root input, & .MuiOutlinedInput-root textarea": {
        border: "none !important", 
        backgroundColor: "transparent !important",
        padding: "0 !important", 
      },
      "& .MuiOutlinedInput-root": {
        backgroundColor: "#1b2e4b !important",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(255, 255, 255, 0.3) !important",
      },

      // 4. LABELS
      "& .MuiInputLabel-root": {
        color: "rgba(255, 255, 255, 0.7) !important",
      },
      "& .MuiInputLabel-shrink": {
        backgroundColor: "#0e1726 !important", 
        paddingRight: "4px",
      },

      // 5. BUTTONS
      "& .MuiButton-root, & button, & .MuiButton-contained": {
        backgroundColor: "#1b2e4b !important",
        color: "#fff !important",
        border: "1px solid rgba(255, 255, 255, 0.2) !important",
      },
      "& .MuiButton-root:hover, & button:hover": {
        backgroundColor: "#3b3f5c !important",
      },

      // 6. TABLES
      "& table, & thead, & tbody, & tfoot, & tr, & th, & td": {
        backgroundColor: "transparent !important",
        color: "#fff !important",
        borderColor: "rgba(255, 255, 255, 0.2) !important",
      },

      // 7. SIGNATURES (CORRECTED FIX)
      "& canvas, & .signature-pad, & img[src*='sign'], & img[alt*='Sign'], & .react-signature-canvas": {
        backgroundColor: "#ffffff !important", 
        filter: "none !important", 
        border: "1px solid #ccc !important", 
        borderRadius: "4px",
        padding: "5px !important",
        maxWidth: "100%",
        display: "block",
      },
    }
  };

  const closeButtonStyle = {
    position: "absolute",
    right: 20,
    top: 10,
    zIndex: (theme) => theme.zIndex.modal + 100,
    color: "red",
    
    // Light Mode
    background: "#f0f0f0",
    "&:hover": { background: "#e0e0e0" },

    // Dark Mode
    ".dark-theme &": {
      background: "#3b3f5c", 
      "&:hover": { background: "#1b2e4b" },
      color: "#ff6b6b"
    },
  };

  const paginationSx = {
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    mt: 2, 
    ".dark-theme &": {
      borderTop: '1px solid #1f2937',
      color: "#fff !important",
      ".MuiTablePagination-selectLabel": { color: "#fff !important" },
      ".MuiTablePagination-displayedRows": { color: "#fff !important" },
      ".MuiTablePagination-select": { color: "#fff !important" },
      ".MuiTablePagination-selectIcon": { color: "#fff !important" },
      ".MuiIconButton-root": { 
         color: "#fff !important",
         "&.Mui-disabled": { color: "rgba(255,255,255,0.3) !important" }
      }
    }
  };

  if (loading)
    return (
      <Box
        sx={{
          height: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );

  return (
    <>
      <GlobalStyles
        styles={{
          "*::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
          },
          "*::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: "#bdbdbd",
            borderRadius: "3px",
          },
          
          // --- HEADER / NAVBAR COLOR FIX (Requested) ---
          ".dark-theme .header-container, .dark-theme .header, .dark-theme .navbar, .dark-theme .MuiAppBar-root, .dark-theme header": {
             backgroundColor: "#0e1726 !important", // Same as card color
             color: "#fff !important",
             boxShadow: "none !important",
             borderBottom: "1px solid #1f2937",
          },

          // 1. DARK MODE DIALOGS (MUI)
          ".dark-theme .MuiDialog-container": {
            "& .MuiDialogTitle-root": {
              backgroundColor: "#0e1726",
              color: "#fff",
            },
            "& .MuiDialogContent-root": {
              backgroundColor: "#0e1726",
            },
            "& .MuiDialogContent-root [style*='border: 1px solid #000']": {
              borderColor: 'rgba(255, 255, 255, 0.3) !important',
            },
            "& .MuiDialogActions-root": {
              backgroundColor: "#0e1726",
            },
            "& .MuiButton-contained": {
              color: "#fff",
            },
          },
          // 2. DARK MODE NOTIFICATIONS
          ".dark-theme .MuiAlert-root, .dark-theme .MuiSnackbarContent-root": {
             backgroundColor: "#1b2e4b !important", 
             color: "#fff !important",
             border: "1px solid #3b3f5c !important",
          },
          ".dark-theme .MuiAlert-icon": {
             color: "#fff !important",
          },

          // 3. DARK MODE SWEETALERT2 POPUPS
          ".dark-theme .swal2-popup": {
             backgroundColor: "#1b2e4b !important",
             color: "#fff !important",
             border: "1px solid #3b3f5c !important",
          },
          ".dark-theme .swal2-title": {
             color: "#fff !important",
          },
          ".dark-theme .swal2-content, .dark-theme .swal2-html-container": {
             color: "#fff !important",
          },
          ".dark-theme .swal2-confirm": {
             backgroundColor: "#4361ee !important",
             color: "#fff !important",
             boxShadow: "none !important",
          },
          ".dark-theme .swal2-cancel": {
             backgroundColor: "#e7515a !important",
             color: "#fff !important",
          },
        }}
      />

      <Box className="breadcrumb-card" sx={breadcrumbSx}>
        <h5>Forms</h5>
        <ul className="breadcrumb">
          <li>
            <Link to="/dashboard">
              <i className="material-symbols-outlined">home</i> Dashboard
            </Link>
          </li>
          <li>Forms</li>
        </ul>
      </Box>

      <Card 
        sx={{ 
          p: { xs: 2, md: 3 }, 
          borderRadius: 2,
          // Explicitly set Card background for dark mode to match breadcrumb and header
          ".dark-theme &": {
            backgroundColor: "#0e1726",
            color: "#fff"
          }
        }}
      >
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isMobile && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {forms.length === 0 ? (
              <Typography align="center">No forms available.</Typography>
            ) : (
              forms.map((f) => (
                <Box
                  key={f.id}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 1.5,
                    p: 1.25,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      #{f.id} {f.Formtitle || f.customFormTitle || "Untitled"}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      Printed: {f.PrintedMark ? "✅" : "❌"} &nbsp; | &nbsp; Sup.
                      Clin.:{" "}
                      {hasSignature(
                        f.supervisedClinicianURL || f.SupervisedClinicianURL
                      )
                        ? "✅"
                        : "❌"}{" "}
                      &nbsp; | &nbsp; Sup.:{" "}
                      {hasSignature(
                        f.supervisorSignURL || f.SupervisorSignURL
                      )
                        ? "✅"
                        : "❌"}{" "}
                      &nbsp; | &nbsp; Intern:{" "}
                      {hasSignature(f.internSignURL || f.InternSignURL)
                        ? "✅"
                        : "❌"}
                    </Typography>
                  </Box>

                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() =>
                      handleView(
                        f.id,
                        f.Formtitle || f.customFormTitle
                      )
                    }
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <TablePagination
                component="div"
                rowsPerPageOptions={[25, 50, 100]}
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </Box>
          </Box>
        )}

        {!isMobile && (
          <TableContainer 
            component={Paper} 
            sx={{ 
              boxShadow: "none", 
              maxHeight: 'calc(100vh - 320px)', 
              overflowY: 'auto',
              "&::-webkit-scrollbar": {
                width: "6px",
                height: "6px",
               
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#bdbdbd",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              // Ensure inner paper is also dark or transparent
              backgroundColor: "transparent", 
              backgroundImage: "none",
            }}
          >
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell sx={stickyHeaderSx}>ID</TableCell>
                  <TableCell sx={stickyHeaderSx}>Form Title</TableCell>
                  <TableCell sx={stickyHeaderSx}>Printed</TableCell>
                  <TableCell align="center" sx={stickyHeaderSx}>Supervised Clinician</TableCell>
                  <TableCell align="center" sx={stickyHeaderSx}>Supervisor Sign</TableCell>
                  <TableCell align="center" sx={stickyHeaderSx}>Intern Sign</TableCell>
                  <TableCell align="center" sx={stickyHeaderSx}>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {forms.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={bodyCellStyle}>
                      <Typography>No forms available.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  forms.map((f) => (
                    <TableRow key={f.id} hover>
                      <TableCell sx={bodyCellStyle}>{f.id}</TableCell>
                      <TableCell sx={bodyCellStyle}>
                        {f.Formtitle || f.customFormTitle || "Untitled"}
                      </TableCell>
                      <TableCell sx={bodyCellStyle}>{f.PrintedMark ? "✅" : "❌"}</TableCell>
                      <TableCell align="center" sx={bodyCellStyle}>
                        {hasSignature(
                          f.supervisedClinicianURL ||
                            f.SupervisedClinicianURL
                        )
                          ? "✅"
                          : "❌"}
                      </TableCell>
                      <TableCell align="center" sx={bodyCellStyle}>
                        {hasSignature(
                          f.supervisorSignURL || f.SupervisorSignURL
                        )
                          ? "✅"
                          : "❌"}
                      </TableCell>
                      <TableCell align="center" sx={bodyCellStyle}>
                        {hasSignature(
                          f.internSignURL || f.InternSignURL
                        )
                          ? "✅"
                          : "❌"}
                      </TableCell>
                      <TableCell align="center" sx={bodyCellStyle}>
                        <IconButton
                          color="primary"
                          onClick={() =>
                            handleView(
                              f.id,
                              f.Formtitle || f.customFormTitle
                            )
                          }
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isMobile && (
           <TablePagination
              component="div"
              rowsPerPageOptions={[25, 50, 100]}
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_e, newPage) => setPage(newPage)}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              sx={paginationSx} 
           />
        )}
      </Card>

      <Modal open={openPreview} onClose={handleClosePreview}>
        <Box sx={modalStyle}>
          <IconButton
            onClick={handleClosePreview}
            sx={closeButtonStyle}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={previewWrapperSx}>
            {previewType === "CollateralNote" && (
              <CollateralNotePreview data={previewData} />
            )}
            {previewType === "ARMHSProgressNote" && (
              <ARMHSProgressNotePreview data={previewData} />
            )}
            {previewType === "ARMHSCrisisPlan" && (
              <ARMHSCrisisPlanPreview data={previewData} />
            )}
            {previewType === "ARMHSDischargeSummary" && (
              <ARMHSDischargeSummaryPreview data={previewData} />
            )}
            {previewType === "ARMHSITP" && (
              <ARMHSITPPreview data={previewData} />
            )}
            {previewType === "ARMHSFunctionalAssessment" && (
              <ARMHSFunctionalAssessmentPreview data={previewData} />
            )}
            {previewType === "ARMHSFunctionalAssessmentNew" && (
              <ARMHSFunctionalAssessmentNewPreview data={previewData} />
            )}
            {previewType === "DiagnosticAssessment" && (
              <DiagnosticAssessmentPreview data={previewData} />
            )}
            {previewType === "AdultDiagnosticAssessment" && (
              <AdultDiagnosticAssessmentPreview data={previewData} />
            )}
            {previewType === "AODASupervisionNote" && (
              <AODASupervisionNotePreview data={previewData} />
            )}
            {previewType === "GAD7" && <GAD7Preview data={previewData} />}
            {previewType === "NurseVisit" && (
              <NurseVisitPreview data={previewData} />
            )}
            {previewType === "PostpartumVisit" && (
              <PostpartumVisitPreview data={previewData} />
            )}
            {previewType === "PrenatalVisit" && (
              <PrenatalVisitPreview data={previewData} />
            )}
            {previewType === "IntakeForm" && (
              <IntakeFormPreview data={previewData} />
            )}
            {previewType === "BirthOverview" && (
              <BirthOverviewPreview data={previewData} />
            )}
            {previewType === "PHQ9" && <PHQ9Preview data={previewData} />}
            {previewType === "BaselineQuestionaire" && (
              <BaselineQuestionairePreview data={previewData} />
            )}
            {previewType === "ClientSessionVerification" && (
              <ClientSessionVerificationPreview data={previewData} />
            )}
            {previewType === "MHISClientForm" && (
              <MHISClientFormPreview data={previewData} />
            )}
            {previewType === "OBGYN" && (
              <OBGYNPreview data={previewData} />
            )}
            {previewType === "Generic" && (
              <Box sx={{ textAlign: "center", p: 3 }}>
                <Typography variant="h6" color="textSecondary">
                  Preview not available
                </Typography>
                <Typography variant="body2">
                  {previewData?.formTitle || "Unknown form"}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default FormsList;